import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Users, Package, Music, Video, Calendar, Settings, 
  ShoppingCart, BarChart3, Plus, Edit2, Trash2, Key,
  Save, X, Eye, EyeOff, ChevronRight, Drum, Image,
  FileText, Radio, Mail, QrCode, CreditCard, Palette,
  GraduationCap, Headphones, Layout, Award, BookOpen
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SiteManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(false);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'editor'
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Orders state
  const [orders, setOrders] = useState([]);

  // Quotes/Bookings state
  const [quotes, setQuotes] = useState([]);

  // Get auth token
  const getAuthHeader = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        await fetchUsers();
      } else if (activeTab === 'orders') {
        await fetchOrders();
      } else if (activeTab === 'quotes') {
        await fetchQuotes();
      } else if (activeTab === 'settings') {
        await fetchStats();
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/admin/users`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to load users');
      }
    } catch (err) {
      toast.error('Failed to load users');
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/api/orders`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      }
    } catch (err) {
      console.error('Orders error:', err);
    }
  };

  const fetchQuotes = async () => {
    try {
      const res = await fetch(`${API}/api/bookings`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setQuotes(data.bookings || []);
      }
    } catch (err) {
      console.error('Quotes error:', err);
    }
  };

  const updateQuoteStatus = async (bookingId, status) => {
    try {
      const res = await fetch(`${API}/api/bookings/${bookingId}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      if (res.ok) {
        setQuotes(prev => prev.map(q => q.id === bookingId ? { ...q, status } : q));
        toast.success(`Marked as ${status}`);
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/admin/stats`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  // User CRUD
  const handleSaveUser = async () => {
    if (!userForm.email) {
      toast.error('Email is required');
      return;
    }
    if (!editingUser && !userForm.password) {
      toast.error('Password is required for new users');
      return;
    }

    try {
      const url = editingUser 
        ? `${API}/api/admin/users/${editingUser.id}`
        : `${API}/api/admin/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      const body = editingUser 
        ? { first_name: userForm.first_name, last_name: userForm.last_name, role: userForm.role }
        : userForm;

      const res = await fetch(url, {
        method,
        headers: getAuthHeader(),
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success(editingUser ? 'User updated!' : 'User created!');
        setShowUserModal(false);
        setEditingUser(null);
        setUserForm({ email: '', password: '', first_name: '', last_name: '', role: 'editor' });
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save user');
      }
    } catch (err) {
      toast.error('Failed to save user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (res.ok) {
        toast.success('User deleted');
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete user');
      }
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!confirm('Reset this user\'s password? They will receive a temporary password.')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Temporary password: ${data.temp_password}`);
        alert(`Temporary password for user: ${data.temp_password}\n\nPlease share this with the user securely.`);
      } else {
        toast.error('Failed to reset password');
      }
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      password: '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role: user.role || 'editor'
    });
    setShowUserModal(true);
  };

  const openNewUser = () => {
    setEditingUser(null);
    setUserForm({ email: '', password: '', first_name: '', last_name: '', role: 'editor' });
    setShowUserModal(true);
  };

  // Content editor links
  const contentEditors = [
    { name: 'Home Slideshow', icon: Image, path: '/admin/hero-slides', color: 'text-orange-400' },
    { name: 'Play Along Editor', icon: Drum, path: '/admin/play-along-editor', color: 'text-orange-500' },
    { name: 'Music Manager', icon: Music, path: '/admin/music', color: 'text-primary' },
    { name: 'Video Manager', icon: Video, path: '/admin/videos', color: 'text-purple-500' },
    { name: 'Class Manager', icon: Calendar, path: '/admin/classes', color: 'text-green-500' },
    { name: 'Live Lessons', icon: Radio, path: '/admin/live-lessons', color: 'text-red-500' },
    { name: 'Gallery', icon: Image, path: '/admin/gallery', color: 'text-blue-500' },
    { name: 'Sample Manager', icon: Headphones, path: '/admin/sample-manager', color: 'text-cyan-500' },
    { name: 'Brasilian Grooves', icon: Drum, path: '/admin/brazilian-grooves', color: 'text-amber-500' },
    { name: 'Course Editor', icon: GraduationCap, path: '/admin/course-editor', color: 'text-indigo-500' },
    { name: 'Kapoeira Editor', icon: Award, path: '/admin/kapoeira-editor', color: 'text-yellow-500' },
    { name: 'Podcasts', icon: Headphones, path: '/admin/podcasts', color: 'text-pink-500' },
    { name: 'QR Codes', icon: QrCode, path: '/admin/qr-codes', color: 'text-gray-500' },
    { name: 'Site Images', icon: Image, path: '/admin/settings', color: 'text-teal-500' },
    { name: 'Theme Editor', icon: Palette, path: '/admin/theme', color: 'text-fuchsia-500' },
    { name: 'Page Builder', icon: Layout, path: '/admin/page-builder', color: 'text-emerald-500' },
    { name: 'Pricing Editor', icon: CreditCard, path: '/admin/pricing-editor', color: 'text-lime-500' },
    { name: 'About Editor', icon: BookOpen, path: '/admin/about-editor', color: 'text-rose-500' },
    { name: 'Contact Messages', icon: Mail, path: '/admin/contact-messages', color: 'text-sky-500' },
  ];

  const tabs = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'quotes', label: 'Quotes & Bookings', icon: Calendar },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4">
        <h1 className="text-2xl font-bold">Site Manager</h1>
        <p className="text-muted-foreground text-sm">Manage your entire site from one place</p>
      </div>

      {/* Tabs */}
      <div className="bg-card border-b border-border px-6">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && !loading && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">All Users ({users.length})</h2>
              <button
                onClick={openNewUser}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-black rounded-lg font-medium hover:bg-primary/90"
              >
                <Plus className="w-5 h-5" />
                Add User
              </button>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Email</th>
                    <th className="text-left px-4 py-3 font-medium">Name</th>
                    <th className="text-left px-4 py-3 font-medium">Role</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => (
                    <tr key={user.id || idx} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3">{user.email}</td>
                      <td className="px-4 py-3">{user.first_name} {user.last_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          user.role === 'super_admin' || user.role === 'admin' 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditUser(user)}
                            className="p-2 hover:bg-muted rounded-lg"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-blue-500" />
                          </button>
                          <button
                            onClick={() => handleResetPassword(user.id)}
                            className="p-2 hover:bg-muted rounded-lg"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4 text-yellow-500" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-2 hover:bg-muted rounded-lg"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CONTENT TAB */}
        {activeTab === 'content' && !loading && (
          <div>
            <h2 className="text-xl font-bold mb-6">Content Editors</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {contentEditors.map((editor) => (
                <motion.button
                  key={editor.path}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(editor.path)}
                  className="bg-card border border-border rounded-xl p-4 text-left hover:border-primary/50 transition-all group"
                >
                  <editor.icon className={`w-8 h-8 ${editor.color} mb-3`} />
                  <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
                    {editor.name}
                  </h3>
                  <ChevronRight className="w-4 h-4 text-muted-foreground mt-2 group-hover:translate-x-1 transition-transform" />
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* ORDERS TAB */}
        {activeTab === 'orders' && !loading && (
          <div>
            <h2 className="text-xl font-bold mb-6">Recent Orders ({orders.length})</h2>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Order ID</th>
                    <th className="text-left px-4 py-3 font-medium">Customer</th>
                    <th className="text-left px-4 py-3 font-medium">Amount</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, idx) => (
                    <tr key={order.id || idx} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono text-sm">{order.id?.slice(0, 8)}...</td>
                      <td className="px-4 py-3">{order.customer_email || order.email || 'N/A'}</td>
                      <td className="px-4 py-3">${order.total?.toFixed(2) || order.amount?.toFixed(2) || '0.00'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          order.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {order.status || 'unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        No orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* QUOTES & BOOKINGS TAB */}
        {activeTab === 'quotes' && !loading && (
          <div data-testid="quotes-tab">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Quote Requests & Bookings ({quotes.length})</h2>
              <button
                onClick={fetchQuotes}
                className="px-4 py-2 bg-card border border-border rounded-lg text-sm hover:border-primary/50"
                data-testid="quotes-refresh-btn"
              >
                Refresh
              </button>
            </div>

            <div className="space-y-3">
              {quotes.map((q) => (
                <div key={q.id} className="bg-card border border-border rounded-xl p-4" data-testid={`quote-row-${q.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold">{q.customer_name}</span>
                        <span className="text-xs text-muted-foreground">#{q.id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${
                          q.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                          q.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                          q.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {q.status || 'pending'}
                        </span>
                        {(!q.performances || q.performances.length === 0) && (
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-primary/15 text-primary font-bold">QUICK QUOTE</span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {q.event_type} • {q.event_date} • {q.location}
                        {q.guest_count ? ` • ${q.guest_count} guests` : ''}
                      </div>
                      {q.details && <div className="text-sm mt-1 text-foreground/80">&ldquo;{q.details}&rdquo;</div>}
                      {q.performances && q.performances.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {q.performances.map(p => p.name).join(', ')} — Total: ${q.total_amount}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={`mailto:${q.customer_email}?subject=Re: Casa Samba Booking Request ${q.id}`}
                        className="px-3 py-1.5 bg-primary text-black rounded-lg text-xs font-bold hover:bg-primary/90"
                        data-testid={`quote-reply-${q.id}`}
                      >
                        Reply
                      </a>
                      <select
                        value={q.status || 'pending'}
                        onChange={(e) => updateQuoteStatus(q.id, e.target.value)}
                        className="bg-background border border-border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        data-testid={`quote-status-${q.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2">
                    {q.customer_email}{q.customer_phone ? ` • ${q.customer_phone}` : ''} • Received {q.created_at ? new Date(q.created_at).toLocaleString() : ''}
                  </div>
                </div>
              ))}
              {quotes.length === 0 && (
                <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground">
                  No quote requests yet
                </div>
              )}
            </div>
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && !loading && (
          <div>
            <h2 className="text-xl font-bold mb-6">Site Statistics</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-card border border-border rounded-xl p-4">
                <Users className="w-8 h-8 text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.total_users || users.length || 0}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <ShoppingCart className="w-8 h-8 text-green-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.total_orders || orders.length || 0}</p>
                <p className="text-sm text-muted-foreground">Total Orders</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <Music className="w-8 h-8 text-purple-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.total_tracks || 0}</p>
                <p className="text-sm text-muted-foreground">Music Tracks</p>
              </div>
              <div className="bg-card border border-border rounded-xl p-4">
                <Video className="w-8 h-8 text-red-500 mb-2" />
                <p className="text-2xl font-bold">{stats?.total_videos || 0}</p>
                <p className="text-sm text-muted-foreground">Videos</p>
              </div>
            </div>

            <h3 className="text-lg font-bold mb-4">Quick Links</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/admin/analytics')}
                className="bg-card border border-border rounded-xl p-4 text-left hover:border-primary/50"
              >
                <BarChart3 className="w-6 h-6 text-primary mb-2" />
                <p className="font-medium">View Analytics</p>
              </button>
              <button
                onClick={() => navigate('/admin/theme')}
                className="bg-card border border-border rounded-xl p-4 text-left hover:border-primary/50"
              >
                <Palette className="w-6 h-6 text-fuchsia-500 mb-2" />
                <p className="font-medium">Theme Settings</p>
              </button>
              <button
                onClick={() => navigate('/admin/contact-messages')}
                className="bg-card border border-border rounded-xl p-4 text-left hover:border-primary/50"
              >
                <Mail className="w-6 h-6 text-sky-500 mb-2" />
                <p className="font-medium">Contact Messages</p>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-card border border-border rounded-2xl p-6 w-full max-w-md"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  disabled={!!editingUser}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg disabled:opacity-50"
                  placeholder="user@example.com"
                />
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg pr-10"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name</label>
                  <input
                    type="text"
                    value={userForm.first_name}
                    onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name</label>
                  <input
                    type="text"
                    value={userForm.last_name}
                    onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                    className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg"
                >
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowUserModal(false)}
                  className="flex-1 px-4 py-2 bg-muted rounded-lg hover:bg-muted/80"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  className="flex-1 px-4 py-2 bg-primary text-black rounded-lg font-medium hover:bg-primary/90 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SiteManager;
